import { filter } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { NetworkService } from '@damofire/data-angular-commons';
import { WarehouseSyncStepContextEvent } from '../sync/executors';
import { logger, ASSET_TYPES_STORE, ASSETS_STORE, IndexedService, INSPECTIONS_STORE, ISSUE_CFGS_STORE, ISSUE_MEDIAS_STORE, ISSUES_STORE, NOTES_STORE, OUTLINES_STORE, SAVED_SEARCH_STORE, SYSTEM2DS_STORE, UPRIGHTS_COLUMNS_STORE, UPRIGHTS_STORE } from '@damofire/data-commons';
import { WarehouseSyncEventService } from '../sync/warehouse-sync-event.service';
import { firstValueFrom } from 'rxjs';

const taskDone = (e: WarehouseSyncStepContextEvent, task: string): boolean => {
  return e.event === 'SYNC_FINISHED' || e.event === 'SYNC_CANCELLED' || (e.event === 'TASK_FINISHED' && e.ctx.task === task);
};
const syncDone = (e: WarehouseSyncStepContextEvent): boolean => e.event === 'SYNC_FINISHED';

@Injectable()
export class LockingIndexedService {
  private readonly LOCK_TASKS_MAP: Record<string, string> = {};

  constructor(
    private readonly warehouseSyncEventService: WarehouseSyncEventService,
    private readonly indexedService: IndexedService,
    private readonly networkService: NetworkService,
  ) {
    this.LOCK_TASKS_MAP[ASSETS_STORE] = 'DOWN_ASS_TASK';
    this.LOCK_TASKS_MAP[ISSUES_STORE] = 'DOWN_ISS_TASK';
    this.LOCK_TASKS_MAP[INSPECTIONS_STORE] = 'DOWN_INS_TASK';
    this.LOCK_TASKS_MAP[NOTES_STORE] = 'DOWN_NOT_TASK';
    this.LOCK_TASKS_MAP[ASSET_TYPES_STORE] = 'DOWN_CFG_TASK';
    this.LOCK_TASKS_MAP[ISSUE_CFGS_STORE] = 'DOWN_CFG_TASK';
    this.LOCK_TASKS_MAP[SYSTEM2DS_STORE] = 'DOWN_SYS_TASK';
    this.LOCK_TASKS_MAP[UPRIGHTS_STORE] = 'DOWN_UPR_TASK';
    this.LOCK_TASKS_MAP[OUTLINES_STORE] = 'DOWN_OUT_TASK';
    this.LOCK_TASKS_MAP[ISSUE_MEDIAS_STORE] = 'DOWN_ISM_TASK';
    this.LOCK_TASKS_MAP[UPRIGHTS_COLUMNS_STORE] = 'DOWN_UPC_TASK';
    this.LOCK_TASKS_MAP[SAVED_SEARCH_STORE] = 'DOWN_SSS_TASK';
  }

  async exists(division: string, storeName: string, key: any): Promise<boolean> {
    await this.lock(storeName, division);
    return this.indexedService.exists(storeName, key);
  }

  async getByKey<T>(division: string, storeName: string, key: any): Promise<T> {
    await this.lock(storeName, division);
    return this.indexedService.getByKey<T>(storeName, key);
  }

  async getAllFromIndex<T>(division: string, storeName: string, indexName: string, key: any): Promise<T[]> {
    await this.lock(storeName, division);
    return this.indexedService.getAllFromIndex(storeName, indexName, key);
  }

  async getAll<T>(division: string, storeName: string): Promise<T[]> {
    await this.lock(storeName, division);
    return this.indexedService.getAll(storeName);
  }

  private async lock(store: string, division: string): Promise<void> {
    if (this.networkService.connected) {
      const waitAfterTask = this.LOCK_TASKS_MAP[store];
      logger.debug('Waiting for task to be finished.', { task: waitAfterTask, store, division, category: 'idb' });

      const warehouseSyncStatus = this.warehouseSyncEventService.watchInputStatus(division);
      if (warehouseSyncStatus) {
        await firstValueFrom(warehouseSyncStatus.pipe(filter((s: WarehouseSyncStepContextEvent) => (waitAfterTask ? taskDone(s, waitAfterTask) : syncDone(s)))));
        logger.debug('Finished waiting for task to be finished.', { task: waitAfterTask, store, division, category: 'idb' });
      }
    }
  }
}
